/**
 * Shared contract for "fire" fields — fields whose job is to trigger a server-side operation, wait
 * for it, and report the outcome rather than to collect input.
 *
 * `ui2:apiField` (executes an apiDefinition) and `ui2:eventField` (fires a storyboard server event)
 * differ only in what they invoke and what comes back. Everything about *when* they fire and *how*
 * they present themselves is identical, and lives here so the two cannot drift apart — a schema
 * author configures these props the same way on either field.
 *
 * Retry props are deliberately absent: the two fields classify failure differently (an
 * apiDefinition returns an HTTP status, a server event does not), so each declares its own
 * `retryType`. `maxAttempts` and `maxAttemptsReachedMessage` mean the same thing for both and stay
 * here.
 */

/**
 * When the field fires.
 *
 * - `onClick` — a trigger button the user presses.
 * - `inlineLookup` — a search-style text input with a round submit button. The user types a query
 *   and submits it; submitting is the fire. Everything after the fire behaves like `onClick`,
 *   except that retry reuses the same submit button instead of rendering a separate one.
 * - `onMount` / `onInputsChanged` / `onCondition` — auto-fire modes with no user affordance.
 */
export type FireMode = 'onClick' | 'inlineLookup' | 'onMount' | 'onInputsChanged' | 'onCondition'

/** Modes that render a field shell (label, help text, error slot) and a user-facing affordance. */
export const isFireInteractiveMode = (mode: FireMode | undefined): boolean =>
  mode === 'onClick' || mode === 'inlineLookup'

/** Fill treatment. `secondary` is outlined over a transparent background; `tertiary` has no border. */
export type FireButtonStyle = 'primary' | 'secondary' | 'tertiary'

/** Intent color the fill/border/text are drawn from. */
export type FireButtonColor =
  'primary' | 'success' | 'info' | 'warning' | 'danger' | 'neutral'

/**
 * A button's appearance. `variant` picks the fill treatment, `color` the intent it is drawn in, so
 * a schema author can ask for e.g. a filled danger button or an outlined success one without the
 * component enumerating every pairing.
 */
export interface FireButtonVariant {
  variant?: FireButtonStyle
  color?: FireButtonColor
}

export const FIRE_DEFAULT_FIRE_BUTTON_VARIANT: Required<FireButtonVariant> = {
  variant: 'secondary',
  color: 'primary',
}

export const FIRE_DEFAULT_RETRY_BUTTON_VARIANT: Required<FireButtonVariant> = {
  variant: 'secondary',
  color: 'danger',
}

export const resolveFireButtonVariant = (
  variant: FireButtonVariant | undefined,
  fallback: Required<FireButtonVariant>,
): Required<FireButtonVariant> => ({
  variant: variant?.variant ?? fallback.variant,
  color: variant?.color ?? fallback.color,
})

export interface FireExpression {
  type: 'expression'
  value: string
}

/** A prop that may be authored as a literal or as an expression resolved against the frames. */
export type ResolvableValue<T> = T | FireExpression

export const isFireExpression = (v: any): v is FireExpression =>
  !!v && typeof v === 'object' && v.type === 'expression' && typeof v.value === 'string'

export interface FireDeepResolveResult<T> {
  value: T
  /** False when any expression in the tree resolved to null/undefined. */
  allPresent: boolean
}

/**
 * Resolves every `{type: 'expression'}` node anywhere in a value, however deeply nested.
 *
 * This exists for the `lazy*` request props. The renderer materializes expressions in ordinary
 * props before the component ever sees them (`abstract-renderer`: any key not starting with
 * `lazy`), which is correct for anything resolvable from the frames — but it means a prop can never
 * reference state that only exists inside the component, like `inlineLookup`'s `lookupQuery`. The
 * `lazy` prefix hands the raw tree to the field, and this walks it with the field's own scope.
 *
 * Nesting is the whole point: `lazyDetails.userLookup.phone` is two levels down, and the fields'
 * per-value resolver only ever inspected the top level.
 */
export const resolveExpressionsDeep = <T>(
  input: T,
  evaluate: (expression: FireExpression) => unknown,
): FireDeepResolveResult<T> => {
  let allPresent = true

  const walk = (node: any): any => {
    if (isFireExpression(node)) {
      const resolved = evaluate(node)
      if (resolved === null || resolved === undefined) allPresent = false
      return resolved
    }
    if (Array.isArray(node)) return node.map(walk)
    // Plain objects only — a Date, a class instance or a React node is a leaf.
    if (node && typeof node === 'object' && Object.getPrototypeOf(node) === Object.prototype) {
      const out: Record<string, unknown> = {}
      for (const key of Object.keys(node)) out[key] = walk(node[key])
      return out
    }
    return node
  }

  return { value: walk(input) as T, allPresent }
}

export interface FireFieldProps {
  fireMode?: FireMode
  /** Fires when this expression transitions falsy→truthy. Used with `fireMode: 'onCondition'`. */
  fireCondition?: string
  /** Clears the bound value when this expression transitions falsy→truthy. */
  clearResponseCondition?: string
  /** Debounce for `fireMode: 'onInputsChanged'`. Default 500. */
  debounceMs?: number

  buttonLabel?: string
  /** Appearance of the fire button. Default: outlined primary. */
  fireButtonVariant?: FireButtonVariant
  /** Appearance of the retry button. Default: outlined danger. */
  retryButtonVariant?: FireButtonVariant

  /**
   * Caps how many attempts a retryable failure may consume. A failure that never reached the server
   * does not count against it.
   */
  maxAttempts?: number
  maxAttemptsReachedMessage?: string

  /**
   * Text shown beside a spinner while the request is in flight — inline text, never a banner.
   *
   * The interactive modes (`onClick`, `inlineLookup`) show a default message when this is unset;
   * the auto-fire modes show nothing at all unless it is set explicitly, since they have no shell
   * to hang a message on and firing is meant to be invisible.
   */
  loadingMessage?: string

  /**
   * Expression evaluated with the field's response in scope, whose result is rendered inside a
   * success banner. The scope variable is named per field — `apiResponse` on apiField,
   * `eventResponse` on eventField.
   *
   * No success banner renders unless this (or `lazySuccessUiSchema`) is set, in any mode.
   */
  successMessageExpression?: string

  /** Failure counterpart of `successMessageExpression`. */
  failureMessageExpression?: string

  /**
   * A uiSchema snippet rendered in place of the success banner, with the field's response in scope.
   * Takes precedence over `successMessageExpression`, which is then ignored.
   *
   * Rendered read-only, through the same `ViewRenderer` path that backs `ui2:selectField` option
   * templates — display components work; bound fields inside it do not write back to the form.
   */
  lazySuccessUiSchema?: any

  /**
   * Failure counterpart of `lazySuccessUiSchema`. When retry is configured, the retry button is
   * rendered directly beneath this custom UI — except in `inlineLookup` mode, where the field's own
   * submit button is the retry affordance and is disabled once the attempt limit is reached.
   */
  lazyFailureUiSchema?: any

  /**
   * With `fireMode: 'onMount'` the field skips the auto-fire when it already holds a value. Set
   * true to fire anyway.
   */
  fireOnMountEvenIfPrefilled?: boolean
  debug?: boolean
}

/** Copy and behavior specific to `fireMode: 'inlineLookup'`. */
export interface FireInlineLookupProps {
  /**
   * Absolute form path the typed query is written to on submit, through the renderer's
   * `overridePath` channel. Optional — the query is always in scope as `lookupQuery` when the
   * field resolves its request inputs, so a path is only needed when something *else* on the form
   * has to read it.
   */
  lookupQueryPath?: string
  /** Placeholder for the lookup input. */
  lookupPlaceholder?: string
  /** Submit stays disabled until the query is at least this long. Default 1. */
  minQueryLength?: number
}

/**
 * A fire field counts as "filled" only once it holds a **successful** response.
 *
 * This is what `isRequired` means on these fields: the point of one is that the server call
 * happened and worked, so an untouched field, a field the user gave up retrying, and a field
 * holding a failed response are all equally unfilled. A failed response is a value — without this,
 * `value != null` would call the field satisfied precisely when it isn't.
 *
 * Wired in two places, which must agree:
 *   - `static isValueEmptyForRequired` on each field class — the renderer's adapter reads it to
 *     synthesize the `is required` error at submit and to auto-expand the containing section.
 *   - `isValueEmpty` passed to the field shell — how the shell decides to paint red.
 */
export const isFireResponseIncomplete = (value: any): boolean => value?.success !== true

export const FIRE_DEFAULT_DEBOUNCE_MS = 500
export const FIRE_DEFAULT_LOADING_MESSAGE = 'Working on it...'

/**
 * The loading text to render, or null for no loading indicator at all. Auto-fire modes stay silent
 * unless the author opted in; interactive modes fall back to the default copy.
 */
export const resolveFireLoadingMessage = (
  props: Pick<FireFieldProps, 'fireMode' | 'loadingMessage'>,
): string | null => {
  if (props.loadingMessage) return props.loadingMessage
  return isFireInteractiveMode(props.fireMode) ? FIRE_DEFAULT_LOADING_MESSAGE : null
}
